import { isEqual, uniqBy } from "lodash";
import { useMemo, useRef, useState } from "react";
import { MData } from "./types";

export interface UseMaskMultiSelectArgs<T extends MData> {
  options?: T[];
  value: T[];
  onChange?: (v?: T[], details?: any) => void;
  onSearch?: (v: string) => void;
  handleBlur?: any;
  name?: string;
  disabled?: boolean;
  readOnly?: boolean;
}

export function useMaskMultiSelect<T extends MData>({
  options,
  value,
  onChange,
  onSearch,
  handleBlur,
  name,
  disabled,
  readOnly,
}: UseMaskMultiSelectArgs<T>) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAllSelected, setShowAllSelected] = useState(false);

  // Options must be objects with stable positional keys: keys[0] = id,
  // keys[1] = display label. Preserved from the original implementation.
  const keys = useMemo(
    () =>
      options && options.length > 0
        ? (Object.keys(options[0]) as (keyof T)[])
        : ([] as (keyof T)[]),
    [options]
  );
  const idKey = keys[0];
  const labelKey = keys[1];

  const allOptions = useMemo(
    () => uniqBy(options ?? [], idKey as string),
    [options, idKey]
  );

  const isSelected = (opt: T) =>
    value.some((v) => isEqual(v[idKey], opt[idKey]));

  // Skip local filtering when parent owns search (server-side filter).
  const filtered = useMemo(() => {
    if (onSearch) return allOptions;
    const q = searchTerm.trim().toLowerCase();
    if (!q) return allOptions;
    return allOptions.filter((o) =>
      String(o[labelKey] ?? "").toLowerCase().includes(q)
    );
  }, [allOptions, searchTerm, onSearch, labelKey]);

  // Drive the selected list from `value` so chips stay visible even when the
  // search term filters them out of `options`.
  const selectedItems = value;
  const unselectedFiltered = useMemo(
    () => filtered.filter((o) => !isSelected(o)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filtered, value]
  );

  const allSelected =
    !!allOptions.length && selectedItems.length === allOptions.length;

  const openPopup = () => {
    if (disabled || readOnly) return;
    setOpen(true);
  };

  const closePopup = () => {
    setOpen(false);
    setSearchTerm("");
    setShowAllSelected(false);
    handleBlur?.({ target: { name } });
  };

  const toggleOption = (opt: T) => {
    const next = isSelected(opt)
      ? value.filter((v) => !isEqual(v[idKey], opt[idKey]))
      : [...value, opt];
    onChange?.(next, { option: opt });
  };

  const toggleSelectAll = () => {
    onChange?.(allSelected ? [] : allOptions, { option: { all: true } });
  };

  const clearAll = () => {
    onChange?.([], { option: { clear: true } });
  };

  const handleSearchChange = (v: string) => {
    setSearchTerm(v);
    onSearch?.(v);
  };

  const toggleShowAllSelected = () => setShowAllSelected((s) => !s);

  return {
    anchorRef,
    open,
    openPopup,
    closePopup,
    searchTerm,
    handleSearchChange,
    showAllSelected,
    toggleShowAllSelected,
    idKey,
    labelKey,
    selectedItems,
    unselectedFiltered,
    allSelected,
    isSelected,
    toggleOption,
    toggleSelectAll,
    clearAll,
  };
}

